import { useIsMutating } from '@tanstack/react-query';
import React, { useState, useSyncExternalStore } from 'react';
import { Alert, Button, Tag } from 'antd';
import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from './AuthProvider';
import { SavedSession } from './session';
import { getPendingWrites, subscribePendingWrites } from '../api/client';

export const environmentColors = { dev: 'blue', staging: 'gold', 'staging-testpay': 'orange', production: 'red', local: 'green', other: 'purple' };
export const SavedSessions: React.FC<{ onReauthenticate(session: SavedSession): void; onSelected?(): void }> = ({ onReauthenticate, onSelected }) => {
  const { sessions, activeSession, switchSession, logout } = useAuth();
  const writes = useSyncExternalStore(subscribePendingWrites, getPendingWrites) + useIsMutating();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError('');
    try { await action(); } catch (err) { setError(err instanceof Error ? err.message : 'Session action failed'); }
    finally { setBusy(false); }
  };
  return <div className="flex flex-col gap-2">
    {error && <Alert type="error" showIcon message={error} />}
    {writes > 0 && <span className="text-slate-500 text-xs">Wait for the current action to finish before changing sessions.</span>}
    {sessions.map(session => <div key={session.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2">
      <Button type="text" className="flex-1 min-w-0 text-left"
        style={{ height: 'auto', padding: '12px 16px', textAlign: 'left', justifyContent: 'flex-start' }} disabled={busy || writes > 0}
        onClick={() => session.needsLogin ? onReauthenticate(session) : void run(async () => { await switchSession(session.id); onSelected?.(); })}>
        <div className="min-w-0">
          <div className="truncate">{activeSession?.id === session.id && <CheckOutlined className="mr-2" />}{session.email}</div>
          <div className="mt-1"><Tag color={environmentColors[session.environment]}>{session.environment === 'other' ? 'CUSTOM' : session.environment.toUpperCase()}</Tag>
            {session.needsLogin && <span className="text-amber-700 text-xs">Sign in again</span>}</div>
          {session.environment === 'other' && <div className="text-xs text-slate-500 truncate" title={session.apiUrl}>{new URL(session.apiUrl).host}</div>}
        </div>
      </Button>
      <Button type="text" danger icon={<DeleteOutlined />} aria-label={`Remove ${session.email} from ${session.environment}`}
        disabled={busy || writes > 0} onClick={() => void run(() => logout(session.id))} />
    </div>)}
  </div>;
};
